import React, { useEffect, useRef, useState } from "react";
import "../styles/Global.css";
import styles from "../components/InputBar.module.css";
import { useLocation } from "react-router-dom";
import {
  CheckIcon,
  ChevronDownIcon,
  PaperAirplaneIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../hooks/useAuth";
import FloatingUpgradeBanner from "./FloatingUpgradeBanner";
import SubscriptionStatusChip from "./SubscriptionStatusChip";

// --------------------------------------------------
// Input length constraints
// --------------------------------------------------
// Maximum number of characters the user can enter in the input field. If the
// user attempts to exceed this number, the extra characters are ignored and a
// global notification is shown.
const MAX_INPUT_CHARS = 1750;
const TEXTAREA_MAX_HEIGHT = 160;

export type ChatDomain = "dentistry" | "medicine";

// Define props for the InputBar component
interface InputBarProps {
  onSend?: (
    message: string,
    inputType: "thread" | "question",
    specialty: string,
    domain: ChatDomain,
    credits: number,
    onAccepted?: () => void
  ) => void | boolean | Promise<void | boolean>;
  isProcessing?: boolean;
  credits?: number;
  subscriptionPlan?: string | null; // "premium" | "premium_plus" | null
  className?: string;
  initialDomain?: ChatDomain;
  onDomainChange?: (domain: ChatDomain) => void;
  compact?: boolean;
}

/**
 * InputBar component - Allows users to input messages or questions
 * Includes a toggle switch to change between thread (agent) and question modes
 */
const InputBar: React.FC<InputBarProps> = ({
  onSend,
  isProcessing = false,
  credits = 0,
  subscriptionPlan = null,
  className = "",
  initialDomain,
  onDomainChange,
  compact = false,
}) => {
  const { session } = useAuth();
  const [input, setInput] = useState("");
  const [isDomainMenuOpen, setIsDomainMenuOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const domainControlRef = useRef<HTMLDivElement>(null);
  const submitLockRef = useRef(false);

  const location = useLocation();
  const isChatPage = location.pathname.includes("/chat");
  const inputType: "thread" | "question" = isChatPage ? "thread" : "question";
  const navigationDomain = (location.state as { domain?: unknown } | null)?.domain;
  const [selectedDomain, setSelectedDomain] = useState<ChatDomain>(
    initialDomain === "medicine" || navigationDomain === "medicine"
      ? "medicine"
      : "dentistry"
  );
  const selectedSpecialty =
    selectedDomain === "medicine" ? "General Medicine" : "General practitioner";
  const selectedDomainLabel =
    selectedDomain === "medicine" ? "Medicine" : "Dentistry";

  useEffect(() => {
    if (navigationDomain === "dentistry" || navigationDomain === "medicine") {
      setSelectedDomain(navigationDomain);
    } else if (initialDomain) {
      setSelectedDomain(initialDomain);
    }
  }, [initialDomain, navigationDomain]);

  useEffect(() => {
    if (!isDomainMenuOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!domainControlRef.current?.contains(event.target as Node)) {
        setIsDomainMenuOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsDomainMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isDomainMenuOpen]);

  useEffect(() => {
    if (isProcessing) {
      setIsDomainMenuOpen(false);
    }
  }, [isProcessing]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    textarea.style.height = "auto";
    const nextHeight = Math.min(textarea.scrollHeight, TEXTAREA_MAX_HEIGHT);
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY =
      textarea.scrollHeight > TEXTAREA_MAX_HEIGHT ? "auto" : "hidden";
  }, [input]);

  // Move handleUpgradeClick to the top level
  const handleUpgradeClick = () => {
    window.location.href = "/pricing";
  };

  const handleSubmit = async () => {
    if (submitLockRef.current) return;
    if (input.trim() && onSend && !isProcessing) {
      submitLockRef.current = true;
      // Always use chat mode on the Chat page; elsewhere keep existing behavior
      const effectiveType: "thread" | "question" = isChatPage
        ? "thread"
        : inputType;
      let accepted = false;
      const clearOnAccepted = () => {
        accepted = true;
        setInput("");
      };
      try {
        const result = await onSend(
          input,
          effectiveType,
          selectedSpecialty,
          selectedDomain,
          credits,
          clearOnAccepted
        );
        if (result !== false && !accepted) {
          setInput("");
        }
      } finally {
        submitLockRef.current = false;
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    if (newValue.length > MAX_INPUT_CHARS) {
      (window as any).showNotification?.(
        `Maximum length of ${MAX_INPUT_CHARS} characters exceeded`,
        "warning"
      );
      return; // Ignore extra characters
    }
    setInput(newValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const selectDomain = (domain: ChatDomain) => {
    setSelectedDomain(domain);
    onDomainChange?.(domain);
    setIsDomainMenuOpen(false);
  };

  // Helper function to get bottom container classes
  const getBottomContainerClasses = (): string => {
    const baseClass = styles.bottomContainer;
    const layoutClass = isChatPage
      ? styles.bottomContainerChat
      : styles.bottomContainerDefault;

    return [baseClass, layoutClass].filter(Boolean).join(" ");
  };

  const submitButtonClasses = [
    styles.submitButton,
    isProcessing ? styles.submitButtonProcessing : "",
    isProcessing || !input.trim()
      ? styles.submitButtonDisabled
      : styles.submitButtonEnabled,
  ]
    .filter(Boolean)
    .join(" ");

  const placeholder = isProcessing
    ? "Processing your message..."
    : isChatPage
      ? `Ask C-Shaped about ${selectedDomain}...`
      : `Ask a specific question about ${selectedDomain}...`;

  return (
    <div className={[styles.mainContainer, compact ? styles.compact : "", className].filter(Boolean).join(" ")}>
      <div className={styles.inputBarContainer}>
        {/* Mode selector removed on Chat page: default is always Chat (Agent4). */}

        <div className={styles.inputContainer}>
          <textarea
            ref={textareaRef}
            value={input}
            // Intercept input changes to enforce the 1750-character limit.
            // If the user tries to go over the limit we:
            //   1) Do NOT update the local state (so the extra characters are not shown)
            //   2) Trigger a non-intrusive global notification so the user knows why
            //      their additional typing had no effect.
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            disabled={isProcessing}
            className={styles.inputField}
            placeholder={placeholder}
            aria-label={isChatPage ? "Chat message" : "Question"}
            rows={1}
          />
          <div className={styles.domainControl} ref={domainControlRef}>
            <button
              type="button"
              className={styles.domainTrigger}
              onClick={() => setIsDomainMenuOpen((open) => !open)}
              disabled={isProcessing}
              aria-label={`Knowledge area: ${selectedDomainLabel}`}
              aria-haspopup="menu"
              aria-expanded={isDomainMenuOpen}
            >
              <span className={styles.domainIndicator} aria-hidden="true" />
              <span className={styles.domainTriggerLabel}>{selectedDomainLabel}</span>
              <ChevronDownIcon
                className={`${styles.domainChevron} ${
                  isDomainMenuOpen ? styles.domainChevronOpen : ""
                }`}
                aria-hidden="true"
              />
            </button>

            {isDomainMenuOpen && (
              <div className={styles.domainMenu} role="menu" aria-label="Knowledge area">
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={selectedDomain === "dentistry"}
                  className={`${styles.domainMenuItem} ${
                    selectedDomain === "dentistry"
                      ? styles.domainMenuItemSelected
                      : ""
                  }`}
                  onClick={() => selectDomain("dentistry")}
                >
                  <span>Dentistry</span>
                  {selectedDomain === "dentistry" && (
                    <CheckIcon className={styles.domainCheck} aria-hidden="true" />
                  )}
                </button>
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={selectedDomain === "medicine"}
                  className={`${styles.domainMenuItem} ${
                    selectedDomain === "medicine"
                      ? styles.domainMenuItemSelected
                      : ""
                  }`}
                  onClick={() => selectDomain("medicine")}
                >
                  <span>Medicine</span>
                  {selectedDomain === "medicine" && (
                    <CheckIcon className={styles.domainCheck} aria-hidden="true" />
                  )}
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            className={submitButtonClasses}
            onClick={handleSubmit}
            disabled={isProcessing || !input.trim()}
            aria-label={isProcessing ? "Processing message" : "Send message"}
            aria-busy={isProcessing}
          >
            {isProcessing ? (
              <span className={styles.loadingSpinner} aria-hidden="true" />
            ) : (
              <PaperAirplaneIcon className={styles.submitIcon} aria-hidden="true" />
            )}
          </button>
        </div>
      </div>
      <div className={getBottomContainerClasses()}>
        <p className={styles.disclaimerText}>
          C-Shaped might make mistakes, so double-check its responses.
        </p>
        {session &&
          (subscriptionPlan ? (
            <SubscriptionStatusChip
              plan={subscriptionPlan}
              size="compact"
              className={styles.subscriptionStatus}
            />
          ) : credits > 0 ? (
            // Display remaining credits for users without a subscription.
            <p className={styles.creditsText}>
              {`Remaining Credits: ${credits}`}
            </p>
          ) : (
            // If credits are zero, display a call-to-action to upgrade.
            <a
              href="/pricing"
              className={styles.creditsText}
              style={{ textDecoration: "underline", fontWeight: 600 }}
            >
              Get Unlimited Credits!
            </a>
          ))}
        {!subscriptionPlan && (credits === 10 || credits === 5) && (
          <FloatingUpgradeBanner
            credits={credits}
            onClick={handleUpgradeClick}
          />
        )}
      </div>
    </div>
  );
};

export default InputBar;
